import { FC, useCallback, useRef } from "react";

import { BannerSource, TicketingFormCategory } from "@simplyk/common";
import { useForm } from "react-hook-form";

import { trpc } from "../../../../../common/helpers/trpc";
import { useTranslate } from "../../../../../common/hooks/useTranslate";
import { useLiveFormAmplitudeEvents } from "../../common/amplitude/useLiveFormAmplitudeEvents";
import { useLiveFormEditorContext } from "../../common/LiveFormEditorContext";
import { useUpdateTicketingDataInPreview } from "../../common/useUpdateLiveTicketingPreview/useUpdateTicketingDataInPreview";
import { useFormSaveAndNavigate } from "../useFormSaveAndNavigate";

import { LiveFormEditorV2StepBannerStateless, StepBannerFormData } from "./LiveFormEditorV2StepBanner.stateless";

import { ImageUploaderHandle } from "@/components/ImageUploader/types";
import { BannerFormat } from "@/dashboard-design-system/RadioGroup";

const SWEEPSTAKES_EXCLUDED_IMAGE_TAGS = ["Raffle"];

export const TicketingLiveEditorStepBanner: FC = () => {
  const { t } = useTranslate();
  const { apiFormObject: apiTicketing, updateApiFormObject } = useLiveFormEditorContext<"ticketing">();
  const { liveFormAmplitudePayload } = useLiveFormAmplitudeEvents();

  const bannerRef = useRef<ImageUploaderHandle>(null);
  const pendingBannerSourceRef = useRef<BannerSource | null | undefined>(undefined);

  const isLegacyBanner = !apiTicketing.isSquareBanner;
  const excludedZeffyImageTags =
    apiTicketing.formCategory === TicketingFormCategory.Sweepstakes ? SWEEPSTAKES_EXCLUDED_IMAGE_TAGS : undefined;

  const defaultValues: StepBannerFormData = {
    bannerFormat: apiTicketing.bannerVideoUrl ? BannerFormat.Video : BannerFormat.Image,
    banner: apiTicketing.bannerUrl || null,
    bannerVideoUrl: apiTicketing.bannerVideoUrl || undefined,
    isBannerUploading: false,
    isSquareBanner: apiTicketing.isSquareBanner ?? false,
    bannerAltText: apiTicketing.bannerAltText ?? null,
  };
  const form = useForm<StepBannerFormData>({ defaultValues });

  const updateBannerFields = trpc.updateTicketingBannerFields.useMutation();
  const { handleSaveAndNext, handleSaveAndBack } = useFormSaveAndNavigate<StepBannerFormData>({
    type: "ticketing",
    form,
    saveData: async (formData) => {
      // Finalize any pending square crop before saving
      const finalizedSquareCropUrl = await bannerRef.current?.finalizeSquareCrop();
      const bannerUrl = finalizedSquareCropUrl ?? formData.banner;
      const isBannerTouched = Boolean(finalizedSquareCropUrl) || form.getFieldState("banner").isDirty;

      // Only send bannerSource if user changed the image during this session
      const bannerSource = pendingBannerSourceRef.current;

      return await updateBannerFields.mutateAsync({
        ticketingId: apiTicketing.id,
        ...(isBannerTouched && {
          bannerUrl,
          bannerAltText: bannerUrl ? (form.getValues("bannerAltText") ?? null) : null,
        }),
        ...(form.getFieldState("bannerVideoUrl").isDirty && { bannerVideoUrl: formData.bannerVideoUrl || null }),
        isSquareBanner: formData.isSquareBanner,
        ...(bannerSource !== undefined && { bannerSource }),
      });
    },
    errorMessage: t("dashboard", "ticketing.error.updateBanner"),
  });

  const handleBannerSourceChange = useCallback((source: BannerSource | null) => {
    pendingBannerSourceRef.current = source;
  }, []);

  useUpdateTicketingDataInPreview({
    form,
    mutate: (formData, liveDataToMutate) => {
      liveDataToMutate.ticketing.bannerUrl = formData.banner;
      liveDataToMutate.ticketing.bannerAltText = formData.banner ? (formData.bannerAltText ?? null) : null;
      liveDataToMutate.ticketing.bannerVideoUrl = formData.bannerVideoUrl || null;
      if (formData.isSquareBanner !== undefined) {
        liveDataToMutate.ticketing.isSquareBanner = formData.isSquareBanner;
      }
    },
  });

  const handleConfirmMigration = useCallback(() => {
    form.setValue("isSquareBanner", true, { shouldDirty: true });
    updateApiFormObject({ ...apiTicketing, isSquareBanner: true });
  }, [form, apiTicketing, updateApiFormObject]);

  const handleLegacyImageChange = useCallback((): Promise<boolean> => {
    // Apply migration to square format when user changes the legacy banner
    handleConfirmMigration();
    return Promise.resolve(true);
  }, [handleConfirmMigration]);

  return (
    <LiveFormEditorV2StepBannerStateless
      form={form}
      handleSaveAndNext={handleSaveAndNext}
      handleSaveAndBack={handleSaveAndBack}
      amplitudePayload={liveFormAmplitudePayload}
      bannerRef={bannerRef}
      isLegacyBanner={isLegacyBanner}
      onLegacyImageChange={handleLegacyImageChange}
      onBannerSourceChange={handleBannerSourceChange}
      fundraiserContext={{ ticketingId: apiTicketing.id }}
      excludedZeffyImageTags={excludedZeffyImageTags}
    />
  );
};
